import {
  formatMailboxDate,
  type MailboxEmail,
} from "@/features/messaging/types/mailboxEmail";
import { resolveMessageIsRead } from "@/features/messaging/lib/messagingApi";

type WsMessagePayload = {
  pk?: number;
  messageId?: string;
  conversationId?: number | string | null;
  conversation_id?: number | null;
  subject?: string;
  body?: string;
  sender?: number;
  senderUserId?: number;
  sender_name?: string;
  senderClient?: string | null;
  buildingId?: number;
  building?: number;
  sentAt?: string;
  sent_at?: string;
  has_been_read?: boolean;
};

function parseSentAtMs(sentAt?: string): number {
  if (sentAt) {
    const parsed = Date.parse(sentAt);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
}

function conversationIdFromPayload(
  raw: WsMessagePayload,
  envelope?: Record<string, unknown>,
): string {
  const fromEnvelope = envelope?.conversationId;
  if (fromEnvelope != null && String(fromEnvelope).trim()) {
    return String(fromEnvelope).trim();
  }
  const explicit = raw.conversationId ?? raw.conversation_id;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim();
  }
  if (raw.pk != null) return String(raw.pk);
  return `conv-${raw.sender ?? raw.senderUserId ?? "unknown"}-${Date.now()}`;
}

function senderLabel(
  senderUserId: number | undefined,
  senderName: string | undefined,
  senderClient?: string | null,
): string {
  if (senderName?.trim()) return senderName.trim();
  if (senderClient === "fronthagd") return "Accueil";
  if (senderClient === "accueilv2") return "Résident";
  if (senderUserId != null) return `Utilisateur #${senderUserId}`;
  return "Expéditeur";
}

export function mapWsMessageToMailboxEmail(
  raw: WsMessagePayload,
  currentUserId: number | null,
  envelope?: Record<string, unknown>,
): MailboxEmail | null {
  const body = (raw.body ?? "").trim();
  if (!body) return null;

  const messageId =
    raw.pk != null
      ? String(raw.pk)
      : (raw.messageId ?? "").trim() || `ws-${Date.now()}`;
  const senderUserId = raw.sender ?? raw.senderUserId;
  const sentAtRaw = raw.sent_at ?? raw.sentAt;
  const sentAtMs = parseSentAtMs(sentAtRaw);
  const sentAt = sentAtRaw?.trim() || formatMailboxDate(new Date(sentAtMs));
  const isFromMe = currentUserId != null && senderUserId === currentUserId;
  const conversationId = conversationIdFromPayload(raw, envelope);

  return {
    id: messageId,
    conversationId,
    subject: (raw.subject ?? "").trim() || "(Sans objet)",
    fromName: isFromMe ? "Moi" : senderLabel(senderUserId, raw.sender_name, raw.senderClient),
    fromEmail: "",
    senderRole: isFromMe ? "Envoyé" : senderLabel(senderUserId, raw.sender_name, raw.senderClient),
    body,
    preview: body.slice(0, 120),
    sentAt,
    sentAtMs,
    isRead: resolveMessageIsRead(isFromMe, raw.has_been_read),
    isFromMe,
    senderUserId,
  };
}

export function extractWsMessage(payload: Record<string, unknown>): WsMessagePayload | null {
  const message = payload.message;
  if (!message || typeof message !== "object") return null;
  return message as WsMessagePayload;
}
